/*
 * FolhaPontoBlocker — bloqueia o app do funcionário até ele enviar a
 * foto da folha de ponto quando houver convocação pendente do admin.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { API, useAuth } from "../App";
import { Button } from "./ui/button";
import { toast } from "sonner";
import {
  CalendarClock, Camera, Loader2, CheckCircle2, AlertTriangle,
} from "lucide-react";

const compLabel = (c) => {
  if (!c) return "";
  const [y, m] = c.split("-");
  const nomes = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho",
    "Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
  return `${nomes[parseInt(m, 10) - 1]}/${y}`;
};

/* Reduz a foto no navegador (máx 1800px, JPEG 85%) p/ upload leve. */
async function downscale(file) {
  try {
    const bmp = await createImageBitmap(file);
    const MAX = 1800;
    const scale = Math.min(1, MAX / Math.max(bmp.width, bmp.height));
    if (scale === 1 && file.size < 3 * 1024 * 1024) return file;
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext("2d").drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((res) =>
      canvas.toBlob(res, "image/jpeg", 0.85)
    );
    return blob || file;
  } catch {
    return file;
  }
}

export default function FolhaPontoBlocker() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/employee/folha-ponto/pending`, {
        params: { token }, timeout: 15000,
      });
      setItems(r.data?.items || []);
    } catch {
      setItems([]); // fail open
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading || items.length === 0) return null;

  const current = items[0];

  const pick = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Envie uma foto (imagem)");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleSend = async () => {
    if (!file) return;
    setSending(true);
    try {
      const small = await downscale(file);
      const fd = new FormData();
      fd.append("token", token);
      fd.append(
        "file",
        new File([small], "folha.jpg", { type: small.type || "image/jpeg" })
      );
      await axios.post(
        `${API}/employee/folha-ponto/${current.id}/upload`, fd,
        { timeout: 90000 }
      );
      toast.success("Folha de ponto enviada!");
      setFile(null);
      if (preview) URL.revokeObjectURL(preview);
      setPreview(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao enviar a foto");
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="fixed inset-0 z-[28] bg-slate-900/85 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="folha-ponto-blocker">
      <motion.div
        initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-cyan-100 flex items-center justify-center shrink-0">
            <CalendarClock className="w-6 h-6 text-cyan-700" />
          </div>
          <div className="flex-1">
            <h2 className="font-bold text-slate-900 text-lg">
              Folha de Ponto — {compLabel(current.competencia)}
            </h2>
            <p className="text-xs text-slate-500">
              A empresa solicitou a foto da sua folha de ponto deste mês.
            </p>
          </div>
        </div>

        {current.status === "rejected" && (
          <div className="rounded-md bg-red-50 border border-red-200 p-3 text-xs text-red-800 flex gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <strong>Sua foto anterior foi rejeitada.</strong>
              <br />
              Motivo: {current.reject_reason || "envie novamente"}
            </div>
          </div>
        )}

        <div className="rounded-md bg-cyan-50 border border-cyan-100 p-3 text-xs text-cyan-900 space-y-1">
          <p>📄 Fotografe a folha inteira, em local iluminado</p>
          <p>✍️ Confira se as assinaturas e o mês estão legíveis</p>
          <p>🔒 O acesso ao app será liberado após o envio</p>
        </div>

        {preview ? (
          <div className="space-y-2">
            <img src={preview} alt="Prévia da folha"
              className="w-full max-h-72 object-contain rounded-md border border-slate-200 bg-slate-50" />
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1"
                onClick={() => { setFile(null); URL.revokeObjectURL(preview); setPreview(null); }}
                disabled={sending}>
                Tirar outra
              </Button>
              <Button className="flex-1 bg-cyan-700 hover:bg-cyan-800 text-white"
                onClick={handleSend} disabled={sending}
                data-testid="folha-send-btn">
                {sending
                  ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
                Enviar foto
              </Button>
            </div>
          </div>
        ) : (
          <Button
            onClick={() => inputRef.current?.click()}
            className="w-full bg-cyan-700 hover:bg-cyan-800 text-white h-12"
            data-testid="folha-pick-btn">
            <Camera className="w-5 h-5 mr-2" />
            Fotografar / escolher foto
          </Button>
        )}
        <input ref={inputRef} type="file" accept="image/*"
          capture="environment" hidden
          onChange={(e) => pick(e.target.files?.[0])} />

        {items.length > 1 && (
          <p className="text-[11px] text-slate-400 text-center">
            Você tem {items.length} folhas pendentes — enviaremos uma por vez.
          </p>
        )}
      </motion.div>
    </motion.div>
  );
}
